/**
 * 端口与槽位 —— 让器材知道自己能连哪里。
 * 对应技术设计 §5.2（`tech/103-化学-LabEngine/001-化学LabEngine技术详细设计.md`）。
 */

export interface Point {
  x: number;
  y: number;
}

export interface Rect {
  x: number;
  y: number;
  width: number;
  height: number;
}

/**
 * 条件用声明式字符串表达，运行期编译成判断函数（§6.2）。
 * P0 只做类型占位与透传，编译器在 P2 的 logic/condition-evaluator.ts 落地。
 */
export type Condition = string;

/** 端口类型：opening | gas_in | gas_out | tube_end | flame | clamp …（§5.2 注释为开放集合） */
export type PortKind = string;

/** 槽位类型：place | clamp_target | heat_zone | water_collect_zone …（§5.2） */
export type SlotKind = string;

export interface Port {
  id: string;
  kind: PortKind;
  /**
   * 给学生看的中文名。缺省时退回 `id` —— 但那是英文标识符，
   * 面向初中生的界面上不该出现 `sealRing` / `tubeHole` 这种词。
   */
  label?: string;
  /** 相对器材局部坐标 */
  anchor: Point;
  /** 热区半径，适配手指/触控板；建议 24-36px（§5.3 体验参数表） */
  radius?: number;
  direction?: "in" | "out" | "both";
  /** 兼容类型，如 "cork.test_tube" */
  accepts: string[];
  /** 如橡胶塞必须已插入试管 */
  requiredState?: Condition[];
}

export interface Slot {
  id: string;
  kind: SlotKind;
  anchor: Point;
  bounds: Rect;
  accepts: string[];
  /**
   * 同时最多容纳几个东西。缺省 **不限**。
   *
   * 端口天然是独占的（一个试管口只能塞一个塞子），但槽位是**区域**：
   * 试管内部要同时装得下高锰酸钾和棉花，水槽里也可以同时放导管口和集气瓶。
   * 早期把槽位也按独占处理，导致「加了药就放不进棉花」——
   * 正是 `bug/023-化学/002-bug.md` 第 1、2 条卡住的地方。
   */
  capacity?: number;
}

/* ────────────────────────────────────────────────────────────────────────
 * 吸附兼容：语义标签
 *
 * §22 红线 1「连接必须基于语义端口和关系图，不能只用坐标重叠判断」。
 * 判据落在这里：**兼容性淘汰发生在距离计算之前**（见 interaction/snap-controller.ts）。
 *
 * 每个可连接点（端口 / 槽位 / 整件器材）对外暴露一组 **tag**，
 * 并用 `accepts` 声明自己愿意跟哪些 tag 结合。tag 三种形态：
 *
 *   ① `${snake(器材类型)}.${端口或槽位 id}`   精确到具体端口，如 `test_tube.mouth`
 *   ② `${端口或槽位 kind}`                     按端口语义类型，如 `heat_target`、`flame`
 *   ③ `${器材分类小写}.${端口或槽位 id}`       按器材族，如 `container.inside`
 *
 * 整件器材（拖整个集气瓶进水槽收集区）只暴露 ① 的对象形态：`gas_bottle`。
 * ────────────────────────────────────────────────────────────────────── */

export type AnchorType = "port" | "slot" | "object";

/** 一个可连接点的完整描述。由 equipment-registry.describeAnchors() 生成。 */
export interface AnchorFacet {
  objectId: string;
  objectType: string;
  /** 端口 / 槽位 id；整件器材为 null */
  anchorId: string | null;
  anchorType: AnchorType;
  /** 端口或槽位的 kind；整件器材为 null */
  kind: string | null;
  /** 端口的方向。槽位与整件器材恒为 "in"（它们只做接收方） */
  direction: "in" | "out" | "both";
  /** 器材局部坐标 */
  anchor: Point;
  /** 命中半径（槽位由 bounds 推导） */
  radius: number;
  tags: readonly string[];
  accepts: readonly string[];
}

/**
 * 兼容判定：**任一方接受对方的任一 tag 即兼容**。
 *
 * 双向而不是单向，是因为技术设计只在一侧声明兼容性 —— §13.2 写的是
 * 「Cork.sealRing accepts TestTube.mouth」，没有反过来再写一遍。
 * 单向判定会让「拖试管靠近塞子」和「拖塞子靠近试管」行为不一致。
 */
export function anchorsCompatible(a: AnchorFacet, b: AnchorFacet): boolean {
  if (a.objectId === b.objectId) return false;
  return (
    a.accepts.some((tag) => b.tags.includes(tag)) || b.accepts.some((tag) => a.tags.includes(tag))
  );
}
